import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MapContainer, TileLayer, WMSTileLayer, Polygon, Popup, Tooltip, LayersControl, Circle, CircleMarker } from 'react-leaflet';
import { LageplanAnsicht } from '../types';
import lageplanService from '../services/lageplanService';
import { useAuth } from '../contexts/AuthContext';
import {
  STATUS_FILL, STATUS_LABEL, ringToLatLngs, ringCentroid, boundsOfGraeber,
  hauptAbteilung, abteilungColorMap, LAGEPLAN_BEARING,
} from '../components/lageplan/leafletUtils';
import './LageplanKarte.css';
import { Icon } from '../components/ui';

// leaflet-rotate expects a global `L`. ES imports are hoisted, so expose L on window AFTER all
// imports and load the plugin via require (runs in statement order, i.e. after the assignment).
(window as any).L = L;
// eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
require('leaflet-rotate'); // patches L.Map with rotation support (rotate/bearing options)

// Default view (center of Germany) when a Friedhof has no grave geometry yet.
const DEFAULT_CENTER: [number, number] = [51.1657, 10.4515];
const DEFAULT_ZOOM = 6;
// Graves are ~1m x 0.6m, so we need to zoom well past OSM's native max (19).
// Leaflet upscales the z19 tiles for zoom 20-23 (overzoom) so plots stay tappable.
const MAX_ZOOM = 23;
const MAX_NATIVE_ZOOM = 19;

const FALLBACK_COLOR = '#6b7280';
// Blauer Punkt mittig auf Gräbern mit einem Tier über 25 kg.
const SCHWERGEWICHT_COLOR = '#2563eb';
// Deckend weiße 1x1-PNG-Kachel für die "Weiß"-Basisebene (wird auf Kachelgröße hochskaliert).
const WHITE_TILE = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAIAAACQd1PeAAAADElEQVR4nGP4//8/AAX+Av4N70a4AAAAAElFTkSuQmCC';
type ColorMode = 'status' | 'abteilung';
const ALL = 'ALL';

// Map rotation so the grave grid lines up. Positive = clockwise; tweak if it turns the wrong way.
const INITIAL_BEARING = LAGEPLAN_BEARING;
// Zoom level used when focusing on the imported features: third-to-last step (MAX_ZOOM - 2).
const FOCUS_ZOOM = MAX_ZOOM - 2;
// Grave-number labels only show once zoomed in enough (otherwise they overlap and clutter).
const LABEL_MIN_ZOOM = 20;
// DOP20 ≈ 20 cm/px (~zoom 20); above this the WMS is upscaled instead of requesting many tiny
// tiles per zoom step — this is the main fix for slow/repeated aerial loading at high zoom.
const WMS_MAX_NATIVE_ZOOM = 20;
// leaflet-rotate options are not in the leaflet typings, so pass them separately as `any`.
// rotateControl shows the compass (drag it to rotate; shift+drag on the map also works).
const ROTATE_OPTIONS = {
  rotate: true,
  bearing: INITIAL_BEARING,
  rotateControl: { closeOnZeroBearing: false },
} as any;

/** Saved map position (sessionStorage), to return to the exact spot after visiting a grave page. */
type SavedView = { lat: number; lng: number; zoom: number; bearing: number };

/**
 * Centers the map on the grave geometry at a fixed zoom whenever the visible set changes.
 * On the very first fit it instead restores `restoreView` (set when returning from a grave detail
 * page), so the employee lands back exactly where they had zoomed in. Later filter changes
 * re-center normally.
 */
const FitBounds: React.FC<{ map: L.Map | null; bounds: L.LatLngBounds | null; restoreView: SavedView | null }> = ({ map, bounds, restoreView }) => {
  const firstFit = useRef(true);
  useEffect(() => {
    if (!map || !bounds) return;
    if (firstFit.current) {
      firstFit.current = false;
      if (restoreView) {
        map.setView([restoreView.lat, restoreView.lng], restoreView.zoom);
        (map as any).setBearing?.(restoreView.bearing);
        return;
      }
    }
    map.setView(bounds.getCenter(), FOCUS_ZOOM);
  }, [map, bounds, restoreView]);
  return null;
};

const LageplanKarte: React.FC = () => {
  const { friedhofId } = useParams<{ friedhofId: string }>();
  const location = useLocation();
  const { isAdmin } = useAuth();
  const [data, setData] = useState<LageplanAnsicht | null>(null);
  const [loading, setLoading] = useState(true);
  const [map, setMap] = useState<L.Map | null>(null);
  const ersteFaerbung = useRef(true);
  const [colorMode, setColorMode] = useState<ColorMode>('status');
  const [filterAbteilung, setFilterAbteilung] = useState<string>(ALL);
  const [bearing, setBearing] = useState<number>(INITIAL_BEARING);
  const [zoom, setZoom] = useState<number>(FOCUS_ZOOM);
  const [userPos, setUserPos] = useState<{ lat: number; lng: number; acc: number } | null>(null);
  const [tracking, setTracking] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [showLabels, setShowLabels] = useState(false);
  // Beim nächsten Standort-Fix einmalig auf die Position zentrieren (nach Button-Klick).
  const recenterRef = useRef(false);
  // Aktuelle Karteninstanz auch als Ref, damit stabile Callbacks (Speichern des Ausschnitts) sie lesen können.
  const mapRef = useRef<L.Map | null>(null);

  const id = friedhofId ? parseInt(friedhofId) : 0;
  const viewKey = `lageplan:view:${id}`;

  // Beim Öffnen einer Grab-Detailseite gemerkten Ausschnitt einmalig wiederherstellen (Restore-Flag
  // aus der Navigation der "Zurück zum Lageplan"-Schaltfläche). Nur einmal beim Mounten lesen.
  const restoreView = useMemo<SavedView | null>(() => {
    if (!(location.state as any)?.restore) return null;
    try {
      const raw = sessionStorage.getItem(viewKey);
      return raw ? (JSON.parse(raw) as SavedView) : null;
    } catch { return null; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Speichert den aktuellen Kartenausschnitt, bevor zur Grab-Detailseite gewechselt wird.
  const saveCurrentView = useCallback(() => {
    const m = mapRef.current as any;
    if (!m) return;
    const c = m.getCenter();
    const view: SavedView = { lat: c.lat, lng: c.lng, zoom: m.getZoom(), bearing: m.getBearing?.() ?? 0 };
    try { sessionStorage.setItem(viewKey, JSON.stringify(view)); } catch { /* sessionStorage evtl. nicht verfügbar */ }
  }, [viewKey]);

  useEffect(() => { mapRef.current = map; }, [map]);

  const load = useCallback(async () => {
    if (!id) return;
    const ansicht = await lageplanService.getAnsicht(id);
    setData(ansicht);
  }, [id]);

  useEffect(() => {
    (async () => {
      try { await load(); }
      catch { toast.error('Lageplan konnte nicht geladen werden'); }
      finally { setLoading(false); }
    })();
  }, [load]);

  // Apply the initial rotation imperatively (react-leaflet calls setView() after construction,
  // which drops the constructor `bearing`) and keep a live readout of the current rotation.
  useEffect(() => {
    if (!map) return;
    const m = map as any;
    const update = () => setBearing(Math.round(((m.getBearing?.() ?? 0) % 360 + 360) % 360));
    const onZoom = () => setZoom(map.getZoom());
    map.on('rotate', update);
    map.on('zoomend', onZoom);
    map.whenReady(() => { m.setBearing?.(INITIAL_BEARING); update(); onZoom(); });
    return () => { map.off('rotate', update); map.off('zoomend', onZoom); };
  }, [map]);

  // Live-Standort des Nutzers verfolgen, solange `tracking` aktiv ist (nur HTTPS/localhost).
  useEffect(() => {
    if (!tracking) return;
    if (!navigator.geolocation) {
      toast.error('Standortbestimmung wird nicht unterstützt');
      setTracking(false);
      return;
    }
    const watchId = navigator.geolocation.watchPosition(
      p => {
        const next = { lat: p.coords.latitude, lng: p.coords.longitude, acc: p.coords.accuracy };
        setUserPos(next);
        if (recenterRef.current && map) {
          map.setView([next.lat, next.lng], FOCUS_ZOOM);
          recenterRef.current = false;
        }
      },
      err => {
        // iOS/Safari meldet PERMISSION_DENIED sofort, wenn die Ortung systemweit aus ist
        // oder die Seite nicht über HTTPS läuft – dann gab es nie einen echten Dialog.
        let msg: string;
        if (err.code === err.PERMISSION_DENIED) {
          msg = window.isSecureContext
            ? 'Standortzugriff abgelehnt. Bitte in iOS unter Einstellungen → Datenschutz → Ortungsdienste → Safari erlauben.'
            : 'Standort nur über HTTPS möglich (nicht über eine HTTP-/IP-Adresse).';
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          msg = 'Standort konnte nicht ermittelt werden (kein GPS-Empfang?).';
        } else if (err.code === err.TIMEOUT) {
          msg = 'Zeitüberschreitung bei der Standortbestimmung.';
        } else {
          msg = 'Standort nicht verfügbar: ' + err.message;
        }
        toast.error(msg);
        setTracking(false);
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, [tracking, map]);

  const handleLocate = () => {
    if (tracking) {
      // Tracking ausschalten und Marker entfernen.
      setTracking(false);
      setUserPos(null);
      return;
    }
    if (!window.isSecureContext) {
      toast.error('Standort ist nur über HTTPS verfügbar (nicht über eine HTTP-/IP-Adresse).');
      return;
    }
    recenterRef.current = true;
    if (userPos && map) map.setView([userPos.lat, userPos.lng], FOCUS_ZOOM);
    setTracking(true);
  };

  // Beim Wechsel in den/aus dem Vollbild ändert sich die Container-Größe; Leaflet muss
  // nach der Layout-Änderung neu vermessen, sonst werden Kacheln/Polygone falsch platziert.
  useEffect(() => {
    if (!map) return;
    const t = window.setTimeout(() => map.invalidateSize(), 250);
    return () => window.clearTimeout(t);
  }, [fullscreen, map]);

  // All graves with usable geometry; colors and section list are derived from the full set
  // (so they stay stable regardless of the active filter).
  const graeber = useMemo(
    () => (data?.graeber ?? []).filter(g => g.polygon && g.polygon.length >= 3),
    [data]
  );
  const autoColors = useMemo(() => abteilungColorMap(graeber), [graeber]);
  const sections = useMemo(() => Object.keys(autoColors), [autoColors]);
  // Admin-configured colors override the auto palette; unconfigured sections keep their auto color.
  const abtColors = useMemo(
    () => ({ ...autoColors, ...(data?.abteilungFarben ?? {}) }),
    [autoColors, data]
  );
  const visibleGraeber = useMemo(
    () => (filterAbteilung === ALL
      ? graeber
      : graeber.filter(g => hauptAbteilung(g.abteilung) === filterAbteilung)),
    [graeber, filterAbteilung]
  );
  const bounds = useMemo(() => boundsOfGraeber(visibleGraeber), [visibleGraeber]);

  // Grabnummern nur, wenn vom Nutzer aktiviert UND weit genug gezoomt (sonst überlappen sie).
  const showLabelsNow = showLabels && zoom >= LABEL_MIN_ZOOM;

  // Umfärben (Status ↔ Abteilung, Abteilungsfilter) blendet die Canvas-Ebene kurz neu ein, statt
  // hart umzuspringen (App.css, .lageplan-umfaerben). Nicht beim ersten Zeichnen.
  useEffect(() => {
    if (!map) return;
    if (ersteFaerbung.current) { ersteFaerbung.current = false; return; }
    const el = map.getContainer();
    el.classList.remove('lageplan-umfaerben');
    void el.offsetWidth; // Reflow, damit die Animation bei schnellem Umschalten neu startet
    el.classList.add('lageplan-umfaerben');
    const t = window.setTimeout(() => el.classList.remove('lageplan-umfaerben'), 600);
    return () => window.clearTimeout(t);
  }, [colorMode, filterAbteilung, map]);

  // Die ~Tausenden Polygone werden memoisiert, damit häufige State-Updates (Dreh-/Zoom-
  // Anzeige, Vollbild, Standort) nicht alle Gräber neu rendern. Neu nur, wenn sich die
  // sichtbare Menge, die Einfärbung oder die Beschriftungs-Sichtbarkeit ändert.
  const gravePolygons = useMemo(() => visibleGraeber.map(g => {
    const color = colorMode === 'status'
      ? (STATUS_FILL[g.status] || FALLBACK_COLOR)
      : (abtColors[hauptAbteilung(g.abteilung)] || FALLBACK_COLOR);
    return (
      <Polygon
        key={g.grabId}
        positions={ringToLatLngs(g.polygon!)}
        pathOptions={{ color, weight: 2, fillOpacity: 0.45 }}
      >
        <Popup>
          <div className="text-sm">
            <div className="font-semibold">{g.abteilung}/{g.grabnummer}</div>
            <div>Status: {STATUS_LABEL[g.status] || g.status}</div>
            {g.tierName && <div>Tier: {g.tierName}{g.tierArt ? ` (${g.tierArt})` : ''}{(g.tierAnzahl ?? 0) > 1 ? ` +${g.tierAnzahl! - 1} weitere` : ''}</div>}
            {g.schwergewicht && (
              <div className="flex items-center gap-1.5 font-medium text-blue-700">
                <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: SCHWERGEWICHT_COLOR }} />Tier über 25 kg
              </div>
            )}
            {g.kundeName && (
              <div>Kunde: {g.kundeId
                ? <Link to={`/kunden/${g.kundeId}`} className="font-medium text-brand-700 hover:underline">{g.kundeName}</Link>
                : g.kundeName}</div>
            )}
            {g.vertragsnummer && <div>Vertrag: {g.vertragsnummer}</div>}
            <Link to={`/graeber/${g.grabId}`} state={{ from: 'lageplan' }} onClick={saveCurrentView} className="mt-2 inline-block font-medium text-brand-700 hover:underline">
              Zur Grab-Detailseite
            </Link>
          </div>
        </Popup>
        {showLabelsNow && (
          <Tooltip permanent direction="center" className="grab-label">{g.grabnummer}</Tooltip>
        )}
      </Polygon>
    );
  }), [visibleGraeber, colorMode, abtColors, showLabelsNow, saveCurrentView]);

  // Blauer Punkt mittig auf Gräbern mit einem schweren Tier (über 25 kg). Eigene Ebene über den
  // Polygonen; nicht interaktiv, damit Taps zum Grab-Polygon (Popup) durchgereicht werden.
  const schwergewichtMarker = useMemo(() => visibleGraeber
    .filter(g => g.schwergewicht)
    .map(g => (
      <CircleMarker
        key={`sg-${g.grabId}`}
        center={ringCentroid(g.polygon!)}
        radius={5}
        interactive={false}
        pathOptions={{ color: '#fff', weight: 1.5, fillColor: SCHWERGEWICHT_COLOR, fillOpacity: 1 }}
      />
    )), [visibleGraeber]);

  if (loading) return <div className="flex items-center justify-center p-8"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div></div>;
  if (!data) return <div className="p-6">Lageplan nicht gefunden. <Link to="/friedhoefe" className="text-brand-600">Zurück</Link></div>;

  const legend: { key: string; label: string; color: string }[] = colorMode === 'status'
    ? Object.entries(STATUS_LABEL).map(([k, label]) => ({ key: k, label, color: STATUS_FILL[k] }))
    : sections.map(s => ({ key: s, label: `Abt. ${s}`, color: abtColors[s] }));

  // Vom Admin in der Verwaltung gewählte Standard-Hintergrundkarte (Fallback: Luftbild).
  const bg = data.lageplanHintergrund || 'LUFTBILD';

  return (
    <div className="p-4 h-full flex flex-col">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-gray-900">{data.name}</h1>
          <p className="text-sm text-gray-500">
            Tippen Sie auf ein Grab für Details
            {filterAbteilung !== ALL && ` · Abteilung ${filterAbteilung}: ${visibleGraeber.length} Gräber`}
          </p>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          {/* Abteilung filter */}
          {sections.length > 0 && (
            <label className="flex items-center gap-1 text-sm text-gray-600">
              Abteilung:
              <select
                value={filterAbteilung}
                onChange={e => setFilterAbteilung(e.target.value)}
                className="border border-gray-300 rounded-lg px-2 py-1 text-sm"
              >
                <option value={ALL}>Alle</option>
                {sections.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
          )}

          {/* Color-by toggle */}
          <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden text-sm">
            {(['status', 'abteilung'] as ColorMode[]).map(mode => (
              <button
                key={mode}
                onClick={() => setColorMode(mode)}
                className={`px-3 py-1 ${colorMode === mode ? 'bg-brand-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                {mode === 'status' ? 'Nach Status' : 'Nach Abteilung'}
              </button>
            ))}
          </div>

          {/* Live rotation readout (drag the compass on the map to change it) */}
          <span className="text-xs text-gray-500 whitespace-nowrap" title="Karten-Drehung – Kompass oben links zum Anpassen ziehen">
            Drehung: {bearing}°
          </span>

          {/* Standort des Nutzers anzeigen / verfolgen */}
          <button
            onClick={handleLocate}
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-sm whitespace-nowrap border ${tracking ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
            title="Eigenen Standort auf der Karte anzeigen"
          >
            <Icon.Standort className="h-4 w-4" />{tracking ? 'Standort aus' : 'Mein Standort'}
          </button>

          {/* Grabnummern ein-/ausblenden (Performance: standardmäßig aus) */}
          <button
            onClick={() => setShowLabels(s => !s)}
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-sm whitespace-nowrap border ${showLabels ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
            title="Grabnummern auf der Karte anzeigen (ab Zoomstufe 20)"
          >
            <Icon.Nummern className="h-4 w-4" />Grabnummern
          </button>

          {/* Vollbildmodus */}
          <button
            onClick={() => setFullscreen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-sm whitespace-nowrap border bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
            title="Karte im Vollbild anzeigen"
          >
            <Icon.Vollbild className="h-4 w-4" />Vollbild
          </button>

          {/* Legend */}
          <div className="flex flex-wrap gap-2">
            {legend.map(item => (
              <span key={item.key} className="inline-flex items-center text-xs text-gray-600">
                <span className="w-3 h-3 rounded-sm mr-1 inline-block" style={{ backgroundColor: item.color }} />{item.label}
              </span>
            ))}
            <span className="inline-flex items-center text-xs text-gray-600" title="Grab mit einem Tier über 25 kg">
              <span className="w-3 h-3 rounded-full mr-1 inline-block border border-white ring-1 ring-gray-300" style={{ backgroundColor: SCHWERGEWICHT_COLOR }} />Tier über 25 kg
            </span>
          </div>
        </div>
      </div>

      {graeber.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3 mb-3 text-sm">
          Für diesen Friedhof sind noch keine georeferenzierten Gräber vorhanden.
          {isAdmin() ? ' Importieren Sie unter „Verwaltung" eine GeoJSON-Datei, um Gräber auf der Karte anzuzeigen.' : ''}
        </div>
      )}

      <div
        className={fullscreen
          ? 'fixed inset-0 z-[1500] bg-gray-100'
          : 'relative flex-1 bg-gray-100 rounded-lg overflow-hidden'}
        style={fullscreen ? undefined : { minHeight: 320 }}
      >
        {fullscreen && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] flex gap-2">
            <button
              onClick={() => setShowLabels(s => !s)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg shadow border text-sm ${showLabels ? 'bg-brand-600 text-white border-brand-600' : 'bg-white/90 text-gray-800 border-gray-300 hover:bg-white'}`}
              title="Grabnummern auf der Karte anzeigen (ab Zoomstufe 20)"
            >
              <Icon.Nummern className="h-4 w-4" />Grabnummern
            </button>
            <button
              onClick={() => setFullscreen(false)}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-white/90 text-gray-800 rounded-lg shadow border border-gray-300 text-sm hover:bg-white"
            >
              <Icon.Close className="h-4 w-4" />Vollbild beenden
            </button>
          </div>
        )}
        <MapContainer
          center={DEFAULT_CENTER}
          zoom={DEFAULT_ZOOM}
          maxZoom={MAX_ZOOM}
          preferCanvas
          style={{ height: '100%', width: '100%' }}
          ref={setMap}
          {...ROTATE_OPTIONS}
        >
          <LayersControl position="topright">
            <LayersControl.BaseLayer checked={bg === 'LUFTBILD'} name="Luftbild (LGLN DOP20)">
              {/* Amtliche Orthophotos Niedersachsen (20 cm), höchste Auflösung für den Standort. */}
              <WMSTileLayer
                url="https://opendata.lgln.niedersachsen.de/doorman/noauth/dop_wms"
                layers="ni_dop20"
                format="image/jpeg"
                version="1.3.0"
                maxZoom={MAX_ZOOM}
                maxNativeZoom={WMS_MAX_NATIVE_ZOOM}
                updateWhenZooming={false}
                keepBuffer={6}
                attribution='&copy; GeoBasis-DE / <a href="https://www.lgln.niedersachsen.de">LGLN</a> (DOP20)'
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer checked={bg === 'KARTE'} name="Karte">
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                maxZoom={MAX_ZOOM}
                maxNativeZoom={MAX_NATIVE_ZOOM}
                updateWhenZooming={false}
                keepBuffer={6}
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer checked={bg === 'WEISS'} name="Weiß (ohne Karte)">
              {/* Deckend weiße Kacheln, damit die Gräber auf weißem Grund liegen. */}
              <TileLayer url={WHITE_TILE} maxZoom={MAX_ZOOM} attribution="" />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer checked={bg === 'SATELLIT'} name="Satellit (Esri)">
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                attribution='Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community'
                maxZoom={MAX_ZOOM}
                maxNativeZoom={MAX_NATIVE_ZOOM}
                updateWhenZooming={false}
                keepBuffer={6}
              />
            </LayersControl.BaseLayer>
          </LayersControl>
          <FitBounds map={map} bounds={bounds} restoreView={restoreView} />
          {userPos && (
            <>
              {/* Genauigkeitskreis – GPS ist bei ~1 m großen Gräbern nicht exakt. */}
              <Circle
                center={[userPos.lat, userPos.lng]}
                radius={userPos.acc}
                pathOptions={{ color: '#2563eb', fillColor: '#2563eb', fillOpacity: 0.1, weight: 1 }}
              />
              <CircleMarker
                center={[userPos.lat, userPos.lng]}
                radius={6}
                pathOptions={{ color: '#fff', weight: 2, fillColor: '#2563eb', fillOpacity: 1 }}
              />
            </>
          )}
          {gravePolygons}
          {schwergewichtMarker}
        </MapContainer>
      </div>
    </div>
  );
};

export default LageplanKarte;
